import { useQueries, useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { GetServerSideProps, PageConfig } from 'next';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useMemo } from 'react';

import SubCategoriesPage from '../../components/pages/v2/categories/sub-category/sub-category';
import { useAppContext } from '../../components/providers/app-context';
import {
  ADVANCED_VIEW_ACTIVE,
  DETAILED_PRICES,
  SHIPPING_LOCATION,
} from '../../static/cookies';
import forceShippingUKForCoUK from '../../utils/force-shipping-uk-for-co-uk';
import { getSortedGroups } from '../../utils/get-sorted-groups';
import { isGlobalUser } from '../../utils/is-global-user';
import { isUKuser } from '../../utils/is-uk-user';
import { isUSuser } from '../../utils/is-us-user';
import getGroups from '../../utils/react-query/query-options/get-groups';
import getGroupsWithStocks from '../../utils/react-query/query-options/get-groups-with-stock';
import getLasEntries from '../../utils/react-query/query-options/get-last-entries';
import getLastVehicles from '../../utils/react-query/query-options/get-last-vehicles';
import getLoggedUser from '../../utils/react-query/query-options/get-logged-user';
import getStock from '../../utils/react-query/query-options/get-stocks';
import getTranslatedSlugs from '../../utils/react-query/query-options/get-translated-slugs';
import getVehicle from '../../utils/react-query/query-options/get-vehicle';
import { redirectForAdvanced } from '../../utils/redirect-for-advanced';
import slugChange301 from '../../utils/slug-change-301';
import validateCategoryPartSlugs from '../../utils/validate-category-part-slugs';

const useData = () => {
  const {
    locale = 'en',
    route,
    query: { category: queryCategory, slug: querySlug },
  } = useRouter();
  const { detailedPrice, shippingLocation } = useAppContext();
  const isUK = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');

  const { vehicle, category } = useMemo(() => {
    if (route === '/auto-parts/[category]/category/[slug]') {
      return {
        vehicle: queryCategory,
        category: querySlug,
      };
    }
    return {
      category: queryCategory,
      vehicle: querySlug,
    };
  }, [queryCategory, querySlug, route]);

  const queries = useMemo(
    () => ({
      queries: [
        getGroupsWithStocks({
          locale,
          ...(vehicle && { vehicle: vehicle as string }),
          ...(shippingLocation ? { shipping_location: shippingLocation } : {}),
        }),
        getTranslatedSlugs({
          lang_origin: locale,
          lang_trans: null,
          slugs: [
            {
              slug_origin: category as string,
              slug_type: 'category',
            },
          ],
        }),
        getLasEntries({
          locale,
          part: '',
          vehicle: vehicle as string || '',
          detailed_prices: !!detailedPrice,
          category: category as string,
        }),
        getLastVehicles({
          locale,
          part: '',
          vehicle: vehicle as string || '',
          category: category as string,
        }),
      ].map(([key, fn]) => ({
        queryKey: key,
        queryFn: fn,
      })),
    }),
    [locale, vehicle]
  );

  const [
    { data: groups },
    { data: translatedSlugs },
    { data: lastEntries },
    { data: vehicles },
  ] = useQueries(queries);

  const { data: vehicleData } = useQuery(...getVehicle({ locale, vehicle }), {
    enabled: !!vehicle,
  });

  const { data: stock } = vehicle
    ? useQuery(
      ...getStock({
        locale,
        no_round: false,
        stocks: [
          {
            category: category,
            ...(vehicle && { vehicle }),
          },
          {
            vehicle: vehicleData?.info?.brand.slug,
          },
        ],
      }),
      {
        enabled: !!vehicle,
      }
    )
    : { data: [] };

  return {
    groups,
    ...(vehicle && { carInfo: vehicleData?.info }),
    lastEntries,
    vehicles,
    vehicle,
    category,
    stock,
    category,
    translatedSlugs,
  };
};

const SubCategories: FunctionComponent<JSX.Element> & PageConfig = ({
  groupsSort,
  brandHistory,
}) => {
  const { groups, ...data } = useData();

  return (
    <main>
      <SubCategoriesPage {...data} groups={groupsSort} brandHistory={brandHistory} />
    </main>
  );
};

export default SubCategories;

export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en',
  req,
  res,
  query,
  params,
}) => {

  isUSuser(req, res);
  isUKuser(req, res);
  isGlobalUser(req, res);
  await forceShippingUKForCoUK({ req, res, locale });
  const isUK = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');

  const { category: categorySlug = '', slug = '' } = query;
  const { default: getGenericClient } = await import('../../server/client');
  const genericClient = getGenericClient();
  const { default: retrieveStateAndClear } = await import(
    '../../utils/retrieve-state-and-clear'
  );
  const { getBrandHistory } = await import('../../services/brand-history');
  if (process.env.NEXT_PUBLIC_TENANT && process.env.NEXT_PUBLIC_TENANT == 5) {
    const { redirectForSB } = await import('../../utils/redirect-for-sb');
    return redirectForSB({
      locale,
      query
    })
  }

  let category, vehicle;
  if (slug) {
    category = slug;
    vehicle = categorySlug;
  } else {
    category = categorySlug;
    vehicle = '';
  }

  try {
    const [user, groups, vehicleData] = await Promise.all([
      genericClient.fetchQuery(...getLoggedUser({ locale }, req)),
      genericClient.fetchQuery(
        ...getGroupsWithStocks(
          {
            locale,
            ...(vehicle && { car_identifier: vehicle as string }),
            ...(req.cookies[SHIPPING_LOCATION]
              ? {
                shipping_location: parseInt(
                  req.cookies[SHIPPING_LOCATION],
                  10
                ),
              }
              : {}),
          },
          req
        )
      ),
      vehicle
        ? genericClient.fetchQuery(
          ...getVehicle(
            {
              locale,
              vehicle,
            },
            req
          )
        )
        : null,
      genericClient.prefetchQuery(
        ...getTranslatedSlugs(
          {
            csrftoken: req.cookies['csrftoken'] || '',
            lang_origin: locale,
            lang_trans: null,
            slugs: [
              {
                slug_origin: category as string,
                slug_type: 'category',
              },
            ],
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ...getLasEntries(
          {
            locale,
            part: '',
            vehicle: vehicle as string || '',
            detailed_prices: req.cookies[DETAILED_PRICES] === 'true',
            category: category as string,
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ...getLastVehicles(
          {
            locale,
            vehicle: vehicle as string || '',
            category: category as string,
          },
          req
        )
      ),
    ]);

    if (vehicle) {
      await genericClient.prefetchQuery(
        ...getStock(
          {
            locale,
            stocks: [
              {
                category: category,
                ...(vehicle && { vehicle }),
              },
              {
                vehicle: vehicleData?.info?.brand.slug,
              },
            ],
          },
          req
        )
      );
    }

    let groupsSort: TCategories = [];
    if (groups) {
      groupsSort = getSortedGroups(groups);
    }

    await validateCategoryPartSlugs({
      locale,
      groups,
      category: category as string,
      req,
    });

    if (user?.staff?.is_pro) {
      return redirectForAdvanced({
        locale,
        vehicle: vehicle as string,
      });
    }

    let brandHistory = null;
    if (vehicleData?.info?.brand?.alias) {
      brandHistory = getBrandHistory({ locale, slug: vehicleData.info.brand.alias });
    }

    return {
      props: {
        dehydratedState: retrieveStateAndClear(genericClient),
        shippingLocation: parseInt(req.cookies[SHIPPING_LOCATION], 10),
        detailPrices: req.cookies[DETAILED_PRICES] || null,
        groupsSort,
        ...(brandHistory && { brandHistory }),
      },
    };
  } catch (e) {
    if (e.status === 301) {
      let newQuery: { [key: string]: string };
      if (params?.slug) {
        newQuery = {
          ...params,
          ...((e.data?.version || e.data?.model) && {
            category: e.data?.version || e.data?.model,
          }),
          ...(e.data?.category && { slug: e.data?.category }),
        };
      } else {
        newQuery = {
          ...params,
          ...(e.data?.category && { category: e.data?.category }),
        };
      }

      return slugChange301({
        locale,
        query: newQuery,
        res,
        page: params?.slug ? 'sub-categories-with-vehicle' : 'sub-categories',
      });
    } else if (e.status === 404) {
      if (e?.data?.detail === 'group_identifier') {
        let newQuery: { [key: string]: string };

        if (params?.slug) {
          newQuery = {
            category: params.category as string,
          };
        } else {
          newQuery = {};
        }

        return slugChange301({
          locale,
          query: newQuery,
          res,
          page: params?.slug ? 'categories-with-vehicle' : 'categories',
          status: 307,
        });
      }

      return {
        notFound: true,
      };
    }

    throw e;
  }
};
